<template>
    <auth-box class="max-w-[480px]" v-if="props.invitation && (!props.sso || passwordForm)">
        <div id="register-form" class="flex flex-col gap-4">
            <div>
                <vx-label> Name </vx-label>
                <vx-input
                    v-model="data.name"
                    data-test="name"
                    autofocus
                    name="name"
                    id="name"
                    size="lg"
                    :error="displayFirstError(v$.name.$errors)"
                >
                </vx-input>
            </div>
            <div>
                <vx-label> Email </vx-label>
                <vx-input
                    :modelValue="data.email"
                    :disabled="true"
                    data-test="email"
                    type="email"
                    name="email"
                    id="email"
                    size="lg"
                    :error="displayFirstError(v$.email.$errors)"
                >
                </vx-input>
            </div>
            <div>
                <vx-label> Password </vx-label>
                <vx-input
                    type="password"
                    v-model="data.password"
                    data-test="password"
                    togglePassword
                    name="password"
                    id="password"
                    size="lg"
                    :error="displayFirstError(v$.password.$errors)"
                ></vx-input>
            </div>

            <div>
                <vx-label> Confirm Password </vx-label>
                <vx-input
                    type="password"
                    v-model="data.password_confirmation"
                    data-test="password_confirmation"
                    togglePassword
                    name="password_confirmation"
                    id="password_confirmation"
                    size="lg"
                    :error="displayFirstError(v$.password_confirmation.$errors)"
                ></vx-input>
            </div>

            <div class="flex flex-col">
                <div class="flex items-center gap-2">
                    <vx-checkbox
                        v-model="data.terms"
                        data-test="terms"
                        name="terms"
                        size="md"
                        :error="displayFirstError(v$.terms.$errors)"
                    ></vx-checkbox>
                    <span class="flex items-center gap-2"
                        >I accept the
                        <a href="/terms" target="_blank"
                            >Terms of Service</a
                        ></span
                    >
                </div>
                <span
                    v-if="v$.terms.$errors?.length"
                    class="mt-2 px-0.5 text-xs text-rose-800"
                >
                    {{ displayFirstError(v$.terms.$errors) }}
                </span>
            </div>

            <div
                class="flex flex-col gap-2 mt-4 md:flex-row-reverse"
            >
                    <vx-button
                        @click.prevent="submit()"
                        data-test="submit"
                        :loading="submitting"
                        class="md:grow"
                        color="primary"
                        size="lg"
                    >
                        Register
                        <font-awesome-icon widthAuto
                            :icon="faArrowRight"
                        ></font-awesome-icon>
                    </vx-button>
            </div>
        </div>
    </auth-box>
    <auth-box class="max-w-[480px]" v-else-if="props.sso && !passwordForm">
        <div class="flex flex-col gap-4 text-center">
            <div class="text-xl font-extrabold">{{ props.sso.team_name }} uses single sign-on</div>
            <div class="text-sm text-slate-500">
                You'll log in to Voxie with your <strong>{{ emailDomain(props.email) }}</strong> account. No password is needed.
            </div>
            <vx-button :href="ssoLoginUrl" color="primary" size="lg" data-test="continue-with-sso">
                Continue with SSO <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
            </vx-button>
            <a v-if="props.sso.password_allowed" href="#" class="text-xs text-slate-600" data-test="create-password" @click.prevent="passwordForm = true">
                Create a password instead
            </a>
        </div>
    </auth-box>
    <auth-box class="max-w-[480px]" :errors="['The invite is either invalid or has expired.']" v-else>
    </auth-box>
</template>
<script setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import AuthBox from "../AuthBox.vue";
import {
    VxLabel,
    VxInput,
    VxButton,
    VxCheckbox,
} from '@voxie/frontend-components';
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { ref, reactive , computed } from "vue";
import useVuelidate from "@vuelidate/core";
import {
    required,
    email as emailRule,
    sameAs,
    maxLength,
    helpers,
    minLength,
} from "@vuelidate/validators";
import { displayFirstError, validateRules } from "~/utils/validation";
import { emailDomain } from '~/utils/emailDomain';
import { stringifyQueryString } from '~/utils/string';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { authService } from "~/services";

const props = defineProps({
    errors: Array,
    email: String,
    invitation: String,
    sso: Object,
});

const { $externalResults, catchError } = useFormRequestHandler();

const data = reactive({
    name: "",
    email: props.email || "",
    password: "",
    password_confirmation: "",
    terms: false,
    invitation: props.invitation,
});

const submitting = ref(false);
const passwordForm = ref(false);

const ssoLoginUrl = computed(
    () => `${props.sso.login_url}?${stringifyQueryString({ invitation: props.invitation })}`
);

const v$ = useVuelidate(
    computed(() => ({
        name: { required, maxLength: maxLength(120) },
        email: { required, email: emailRule, maxLength: maxLength(255) },
        password: {
            required,
            minLength: minLength(12),
            maxLength: maxLength(64),
        },
        password_confirmation: {
            required,
            sameAs: helpers.withMessage(
                "The password confirmation does not match.",
                sameAs(data.password)
            ),
        },
        terms: {
            required,
            checked: helpers.withMessage(
                "The terms field is required.",
                sameAs(true)
            ),
        },
    })),
    data,
    { $externalResults }
);


const submit = async () => {
    submitting.value = true;

    const isFormCorrect = await validateRules(v$);

    if (!isFormCorrect) {
        submitting.value = false;
        return;
    }

    try {
        const response = await authService.register(data);

        window.location.href = response.data?.redirect || "/";
    } catch (error) {
        catchError(error);
    } finally {
        submitting.value = false;
    }
};

Object.keys(window.sessionStorage)
    .filter((key) => {
        return /displayedNotActiveTeam/.test(key);
    })
    .forEach((key) => {
        window.sessionStorage.removeItem(key);
    });
</script>
